import React, { useCallback, useEffect, useState } from 'react';
import Shimmer from 'sushiweb/Shimmer';
import toast from 'react-hot-toast';
import './User.scss';
import { Label, Modal, Tooltip, CopyText, Confirm } from '../../../UiKit';
import { black, orange } from 'sushiweb/tokens/color';
import {
  GoldMembership,
  FdpDetails,
  UserContext,
  DelightFeature,
} from '../../popups';
import { userModel } from '../../../api';
import { downTriangle } from '../../../images/images';
import { getMilliseconds } from '../../commons/CommonMethods';
import { sendEventToJumbo } from '../../../helpers/analytics';
import { OFSEDetails } from './atoms/OFSEDetails';
import { DelightFeatureTag } from './atoms/DelightFeatureTag';
import { UserEmail } from './atoms/UserEmail';
import { AlternateNumber } from './atoms/AlternateNumber';
import { UserNumber } from './atoms/UserNumber';
import {
  sendUserApiEvents,
  sendUserUnmaskEvent,
  sendUserModalEvent,
  USER_EVENTS,
} from '../../../helpers/analytics/user.analytics';
import { UserCity } from './atoms/UserCity';
import { UserTagDetailsModal } from '../Order/OrderBasicInfo/UserTagDetailsModal';
import { Cross } from 'sushiweb/Icons';

export const User = ({
  orderId = null,
  userId = null,
  isWFH = false,
  isNewUser = false,
  showCxContext = true,
  cartId = null,
}) => {
  const [user, setUser] = useState(null);

  useEffect(() => {
    getUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, userId]);

  const getUserData = useCallback(() => {
    let queryStrings = {};
    queryStrings['order_id'] = orderId;
    if (isNewUser) {
      queryStrings['user_id'] = userId;
    }

    userModel
      .getUserData(queryStrings)
      .then((response) => {
        if (!response || response?.isError) return null;
        sendUserApiEvents(
          USER_EVENTS.GET_USER_DETAILS_SUCCESS,
          orderId,
          userId,
        );
        setUser({ ...response });
      })
      .catch(() => {
        sendUserApiEvents(
          USER_EVENTS.GET_USER_DETAILS_FAILURE,
          orderId,
          userId,
        );
      });
  }, [orderId, userId, isNewUser]);

  return (
    <div className='userContainer'>
      {!user ? (
        <Shimmer height='130px' width='100%' />
      ) : (
        <UserComp
          orderId={orderId}
          isWFH={isWFH}
          user={user}
          userId={userId}
          isNewUser={isNewUser}
          showCxContext={showCxContext}
          cartId={cartId}
        />
      )}
    </div>
  );
};

const UserComp = ({
  user,
  orderId,
  isWFH,
  userId,
  isNewUser = false,
  showCxContext,
  cartId = null,
}) => {
  const [userNumberInfo, setUserNumberInfo] = useState(null);
  const [userEmailInfo, setUserEmailInfo] = useState(null);
  const [ofseInfo, setOfseInfo] = useState(null);
  const [altContact, setAltContact] = useState(null);
  const [popupData, setPopupData] = useState(null);
  const [confirmationDetails, setConfirmationDetails] = useState(null);
  const [isOfseClicked, setOfseClicked] = useState(false);
  const [isAltContactClicked, setAltContactClicked] = useState(false);
  const [orderTagDetailsPopUp, setorderTagDetailsPopUp] = useState();
  const [orderTagPopUpInfo, setOrderTagPopUpInfo] = useState();
  const [customerDeviceId, setCustomerDeviceId] = useState(null);
  const closeOrderTagPopUp = () => setorderTagDetailsPopUp(false);
  const calledTo = 'Customer';

  const handleTagDetailsPopup = (tag) => {
    if (tag.meta?.data) {
      setorderTagDetailsPopUp((prevState) => !prevState);
      setOrderTagPopUpInfo(tag.meta);
    }
  };

  const openMembershipModal = useCallback(() => {
    sendUserModalEvent(USER_EVENTS.USER_MEMBERSHIP_MODAL_OPEN, orderId, userId);
    let details = {
      title: 'Pro Membership',
      size: 'large',
      component: (
        <GoldMembership userId={user.id || userId} orderId={orderId} />
      ),
    };
    setPopupData(details);
  }, [user, userId, orderId]);

  const maskUserInfo = useCallback(() => {
    if (!userNumberInfo || (userNumberInfo && !userNumberInfo.valid_till))
      return;
    const { valid_till } = userNumberInfo;
    const getValidMilliseconds = getMilliseconds(valid_till);
    const timerId = setTimeout(() => {
      setUserNumberInfo(null);
      toast.error(userNumberInfo.message ?? 'Masked number validity expired!');
      clearTimeout(timerId);
    }, getValidMilliseconds * 1000);
  }, [userNumberInfo]);

  const makeCallForUnmaskUser = useCallback(
    (type, isForced) => {
      let queryStrings = {
        order_id: orderId,
      };
      if (isNewUser) {
        queryStrings['user_id'] = userId;
      }
      if (isForced) {
        queryStrings['force_allocate'] = isForced;
      }

      return userModel
        .unmaskUserInfo(queryStrings, type)
        .then((response) => {
          if (
            !response ||
            (response && response.isError === true) ||
            (response && response.status === 'failure')
          ) {
            toast.error(
              response.message ||
                'Something went wrong. please try again later!',
            );
          } else {
            return response;
          }
        })
        .catch((err) => {
          toast.error(
            err.message || 'Something went wrong. please try again later!',
          );
        });
    },
    [orderId, userId, isNewUser],
  );

  const unmaskUserNumber = useCallback(
    (isForced) => {
      sendUserUnmaskEvent(
        USER_EVENTS.UNMASK_USER_NUMBER_EVENT,
        orderId,
        userId,
      );
      makeCallForUnmaskUser('number', isForced).then((res) => {
        setUserNumberInfo(res);
        maskUserInfo();
      });
    },
    [orderId, userId, makeCallForUnmaskUser, maskUserInfo],
  );

  const unmaskUserOfseNumber = useCallback(
    (isForced = false) => {
      sendUserUnmaskEvent(
        USER_EVENTS.UNMASK_USER_OFSE_NUMBER_EVENT,
        orderId,
        userId,
      );
      makeCallForUnmaskUser('ofse_number', isForced).then((res) => {
        setOfseInfo(res);
      });
    },
    [orderId, userId, makeCallForUnmaskUser],
  );

  const unmaskUserAltNumber = useCallback(
    (isForced = false) => {
      sendUserUnmaskEvent(
        USER_EVENTS.UNMASK_USER_ALTERNATE_NUMBER_EVENT,
        orderId,
        userId,
      );
      makeCallForUnmaskUser('alternative_number', isForced).then((res) => {
        setAltContact(res);
      });
    },
    [orderId, userId, makeCallForUnmaskUser],
  );

  const unmaskUserEmail = useCallback(() => {
    sendUserUnmaskEvent(USER_EVENTS.UNMASK_USER_EMAIL_EVENT, orderId, userId);
    makeCallForUnmaskUser('email').then((res) => {
      setUserEmailInfo(res);
    });
  }, [orderId, userId, makeCallForUnmaskUser]);

  const reGeneratedMaskNumber = useCallback(
    (details, isOfse = false, isAlt = false) => {
      let eventName = isAlt
        ? USER_EVENTS.REGENERATE_USER_ALTERNATE_NUMBER_EVENT
        : isOfse
        ? USER_EVENTS.REGENERATE_USER_OFSE_NUMBER_EVENT
        : USER_EVENTS.REGENERATE_USER_NUMBER_EVENT;
      sendUserUnmaskEvent(eventName, orderId, userId);
      setConfirmationDetails(details);
      setOfseClicked(isOfse);
      setAltContactClicked(isAlt);
    },
    [orderId, userId],
  );

  const handleConfirmClick = useCallback(
    (action) => {
      let isForced = true;
      setConfirmationDetails(null);

      if (action === 'cancel') {
        return;
      }

      if (isOfseClicked) {
        unmaskUserOfseNumber(isForced);
      } else if (isAltContactClicked) {
        unmaskUserAltNumber(isForced);
      } else {
        unmaskUserNumber(isForced);
      }
    },
    [
      isOfseClicked,
      isAltContactClicked,
      unmaskUserOfseNumber,
      unmaskUserAltNumber,
      unmaskUserNumber,
    ],
  );

  const showFdpDetails = useCallback(
    (data) => () => {
      sendUserModalEvent(USER_EVENTS.USER_FDP_MODAL_OPEN, orderId, userId);
      let details = {
        title: 'Free delivery pack details',
        size: 'medium',
        component: <FdpDetails content={data} />,
      };
      setPopupData(details);
    },
    [orderId, userId],
  );

  const showDelightFeatureDetails = useCallback(() => {
    sendUserModalEvent(
      USER_EVENTS.USER_DELIGHT_FEATURE_MODAL_OPEN,
      orderId,
      userId,
    );
    let details = {
      title: 'User’s Delight Feature',
      size: 'large',
      component: <DelightFeature userId={user.id || userId} />,
    };
    setPopupData(details);
  }, [user, orderId, userId]);

  const openUserContextModal = useCallback(() => {
    if (!showCxContext) return;
    sendUserModalEvent(USER_EVENTS.USER_CONTEXT_MODAL_OPEN, orderId, userId);
    let details = {
      title: 'Customer Overview',
      size: 'large',
      maxHeight: '80vh',
      label: user?.user_karma?.karma,
      bgColor: user?.user_karma?.karma_btn_color,
      color: user?.user_karma?.karma_txt_color,
      isMaxContent: true,
      component: (
        <div className='user_context'>
          <UserContext
            orderId={orderId}
            userId={user.id || userId}
            currentKarma={{
              label: user?.user_karma?.karma,
              bgColor: user?.user_karma?.karma_btn_color,
              textColor: user?.user_karma?.karma_txt_color,
            }}
            setCustomerDeviceId={setCustomerDeviceId}
          />
        </div>
      ),
    };
    setPopupData(details);
  }, [user, orderId, userId]);

  const parentMessageListner = useCallback(
    (event) => {
      const { data: { source = '', action = '', additionalData = {} } = {} } =
        event;
      if (source === 'oneSupport' && action === 'openCustomerContext' && user) {
        if (
          Number(additionalData.transactionId || 0) === Number(orderId || 0) &&
          Number(additionalData.userId) === Number(user.id)
        ) {
          openUserContextModal();
          sendEventToJumbo('smartplug_options_event_click', {
            action: action,
            order_id: String(orderId || 0),
            user_id: String(user.id),
          });
        }
      }
    },
    [user, orderId, openUserContextModal],
  );

  useEffect(() => {
    window.addEventListener('message', parentMessageListner);
    return () => {
      window.removeEventListener('message', parentMessageListner);
    };
  }, [parentMessageListner]);

  const handleModalClose = useCallback(() => {
    setPopupData(null);
    sendUserModalEvent(USER_EVENTS.USER_MODAL_CLOSE, orderId, userId);
  }, [orderId, userId]);

  return (
    <div className='userInfo' data-dd-privacy='mask'>
      {orderTagDetailsPopUp ? (
        <UserTagDetailsModal
          handleClose={closeOrderTagPopUp}
          tagInfo={orderTagPopUpInfo}
        />
      ) : null}
      <div className='lighter wrap'>
        <span role='img' aria-label='customer'>
          🤴🏻
        </span>
        <span>Customer&nbsp;#</span>
        <CopyText title={user.id} />
      </div>
      <h3 className='userName dark'>
        <p
          className='nameStr dark large colorGreen'
          onClick={openUserContextModal}
        >
          {user.name}
        </p>
      </h3>
      {(user?.user_karma?.karma ||
        user.has_gold_subscription ||
        user.concierge_data ||
        user.fdp ||
        user.cod_block_reason ||
        user.is_blocked ||
        user.tags?.employee_details) && (
        <div className='membership'>
          {user?.user_karma?.karma && (
            <Tooltip
              title={
                <div className='karmaLabel'>
                  <Label
                    textColor={user?.user_karma?.karma_txt_color}
                    title={user?.user_karma?.karma}
                    bgColor={user?.user_karma?.karma_btn_color}
                    showIcon={false}
                  />
                </div>
              }
              position={'bottom'}
              border={false}
              label={user?.user_karma?.karma_source}
            />
          )}
          {user?.tags?.employee_details && (
            <Tooltip
              title={
                <div className='employee-details-tooltip'>
                  <Label
                    textColor={user.tags.employee_details.color}
                    title={user.tags.employee_details.text || 'Employee'}
                    bgColor={user.tags.employee_details.bg_color}
                  />
                </div>
              }
              position='bottom'
              border={false}
            />
          )}
          {user.has_pro_subscription ? (
            <div className='gold' onClick={openMembershipModal}>
              <Label
                title='Pro'
                bgColor={user.pro_btn_color}
                showIcon={true}
                icon={downTriangle}
              />
            </div>
          ) : null}
          {user.concierge_data && user.concierge_data.tag ? (
            <Tooltip
              position='right'
              border={false}
              title={
                <div>
                  <Label
                    title={user.concierge_data.tag}
                    bgColor={user.concierge_data.color}
                    showIcon={true}
                    icon={downTriangle}
                  />
                </div>
              }
              label={user.concierge_data.reason}
            />
          ) : null}
          {!!user.fdp?.should_show_fdp_info && (
            <div className='pointer ' onClick={showFdpDetails(user.fdp)}>
              <Label
                title={user.fdp.title}
                bgColor={user.fdp.color}
                showIcon={true}
                icon={downTriangle}
              />
            </div>
          )}
          {!!user.cod_block_reason?.length && (
            <Tooltip
              position='right'
              border={false}
              title={
                <div>
                  <Label
                    title='COD blocked'
                    bgColor={orange.z500}
                    showIcon={false}
                  />
                </div>
              }
              label={user.cod_block_reason}
            />
          )}
          {user.delete_status && (
            <Tooltip
              title={
                <div>
                  <Label
                    textColor={user.delete_status.text_color}
                    title={user.delete_status.label}
                    bgColor={user.delete_status.label_btn_color}
                    showIcon={false}
                  />
                </div>
              }
              position={'bottom'}
              border={false}
              label={user.delete_status.hover_text}
            />
          )}
          {user.user_blocking_details?.is_blocked && (
            <Tooltip
              title={
                <div className='karmaLabel'>
                  <Label
                    title={'Blocked'}
                    bgColor={user.user_blocking_details?.blocked_btn_color}
                    showIcon={false}
                  />
                </div>
              }
              position={'bottom'}
              border={false}
              label={user?.user_blocking_details?.reason}
            />
          )}
          {user.ofse_details?.label && (
            <Tooltip
              title={
                <div className='karmaLabel'>
                  <Label
                    title={user.ofse_details.label}
                    bgColor={user.ofse_details.label_btn_color}
                    showIcon={false}
                  />
                </div>
              }
              position={'bottom'}
              border={false}
              label={user.ofse_details.hover_text}
            />
          )}
          {user.bistro_fraud_details?.text && (
            <Tooltip
              title={
                <div
                  onClick={() => {
                    handleTagDetailsPopup(user.bistro_fraud_details);
                  }}
                  className={`${
                    user.bistro_fraud_details.meta?.data
                      ? 'tw-cursor-pointer'
                      : 'tw-cursor-auto'
                  }`}
                >
                  <Label
                    title={user.bistro_fraud_details.text}
                    bgColor={user.bistro_fraud_details.bg_color}
                    textColor={user.bistro_fraud_details.color}
                    showIcon={false}
                  />
                </div>
              }
              position={'bottom'}
              border={false}
            />
          )}
        </div>
      )}
      <UserNumber
        number={user.number}
        numberInfo={userNumberInfo}
        calledTo={calledTo}
        isWFH={isWFH}
        orderId={orderId}
        userId={userId}
        isNewUser={isNewUser}
        unmaskNumber={unmaskUserNumber}
        regenerateNumber={reGeneratedMaskNumber}
        hasAlternativeContact={user.has_alternative_contact}
        cartId={cartId}
      />
      {user.has_alternative_contact && (
        <AlternateNumber
          number={user.alternative_number}
          numberInfo={altContact}
          calledTo={calledTo}
          isWFH={isWFH}
          orderId={orderId}
          userId={userId}
          isNewUser={isNewUser}
          unmaskNumber={unmaskUserAltNumber}
          regenerateNumber={reGeneratedMaskNumber}
          alternateContactName={
            user?.alternative_contact_name || 'Order Contact Number'
          }
          cartId={cartId}
        />
      )}
      {/* {dashboardMode !== 'smartplug' && ( */}
      <UserEmail emailInfo={userEmailInfo} onClick={unmaskUserEmail} />
      {/* )} */}
      {!!user.city && <UserCity city={user.city} />}
      {user.delight_feature ? (
        <DelightFeatureTag
          featureDetails={user.delight_feature}
          onClick={showDelightFeatureDetails}
        />
      ) : null}
      {user.ofse_data ? (
        <OFSEDetails
          number={user?.ofse_data?.number}
          user={user}
          ofseInfo={ofseInfo}
          calledTo={calledTo}
          isWFH={isWFH}
          orderId={orderId}
          userId={userId}
          isNewUser={isNewUser}
          unmaskNumber={unmaskUserOfseNumber}
          regenerateNumber={reGeneratedMaskNumber}
          cartId={cartId}
        />
      ) : null}
      {popupData && (
        <Modal
          type={popupData?.size}
          padding={0}
          containerStyles={{ margin: '16px 0 20px 0', padding: '0 20px' }}
          isVisible={true}
          paddingTop={'5px'}
          maxHeight={popupData?.maxHeight}
          titleComponent={
            <div className='tw-flex tw-justify-between tw-items-center tw-w-full tw-mb-1'>
              <div className='tw-flex tw-items-start tw-flex-col tw-justify-center tw-gap-2 tw-flex-grow tw-shrink-0 tw-basis-0 tw-text-[18px]'>
                <div className='tw-self-stretch tw-text-grey-900 tw-text-[20px] tw-font-medium'>
                  {popupData?.title}
                </div>
                {customerDeviceId && (
                  <div className='tw-font-medium tw-text-grey-800'>
                    <span>Device ID:</span>
                    <CopyText title={customerDeviceId} />
                  </div>
                )}
              </div>
              <div
                className='tw-mr-6 tw-cursor-pointer'
                onClick={handleModalClose}
              >
                <Cross size={20} color={black} />
              </div>
            </div>
          }
          showCloseIcon={false}
          handleClose={handleModalClose}
          component={popupData.component}
        />
      )}
      {confirmationDetails ? (
        <Confirm
          show={true}
          content={confirmationDetails}
          close={() => setConfirmationDetails(null)}
          handleClick={handleConfirmClick}
        />
      ) : null}
    </div>
  );
};
